import React, { useCallback, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { UI_CONSTANTS } from '../constants';
import { GeneralButton } from 'sharedComponents/GeneralButton';
import { translate } from 'helpers/translateHelper';
import { useHierarchySearch } from 'sharedComponents/HierarchyView/HierarchyHooks/useHierarchySearch';
import { useUserCapability } from 'sharedHooks';
import { IconAdd, IconDragEqual, IconEdit, IconInfo, IconKebab, IconPlus } from 'svgIcons/MotionPortalIcons';
import colors from 'theme/_colors.scss';
import {
    findContainingSite,
    findParentNode,
    applyPendingChangesToTree,
    createAPIRequestData,
    removeTempInputNodes,
    getLocationDepth,
    findSiblingLocations,
} from './helpers';
import Tippy from '@tippy.js/react';
import HierarchyLocationModal from './HierarchyLocationModal';
import { hierarchyLocationModalTypes, NODE_MODIFICATION_TYPE, NODE_TYPE, PENDING_MOVE_TYPE } from './constants';
import HierarchyLeftPanelHeader from './HierarchyLeftPanelHeader';
import HierarchyLeftContent from './HierarchyLeftContent';
import HierarchyInfoModal from './HierarchyInfoModal';
import HierarchyErrorModal from './HierarchyErrorModal';
import HierarchyDuplicateNameModal from './HierarchyDuplicateNameModal';
import { DROP_POSITION } from 'sharedComponents/HierarchyView/constants';
import HierarchyPowertrainInfoModal from './HierarchyPowertrainInfoModal';
import { getSiteIds } from '../helpers';

const HierarchyLeftPanel = (props) => {

    const {
        isEditHierarchy,
        setIsEditHierarchy,
        setPowertrainWizardVisible,
        setWizardSelectedNode,
        getHierarchyDataRequest,
        updateHierarchyDataRequest,
        organizations,
        selectedIds,
        setSelectedIds,
        nodeMap,
        baseHierarchyTree,
        setBaseHierarchyTree,
        expandedIds,
        setExpandedIds,
        flattenedNodes,
        hierarchyRequestData,
        setHierarchyRequestData,
        setSelectedPowertrain,
        filteredPowertrains,
        updateHierarchyLoadingStatus,
        hierarchyErrorModalVisible,
        isSaveFailed,
        setSaveFailed,
        isPartialPowertrainFailure,
        setPartialPowertrainFailure,
        setHierarchyErrorModal,
        selectedSites
    } = props;

    const [pendingChanges, setPendingChanges] = useState(null);
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [locationModalType, setLocationModalType] = useState(null);
    const [selectedNodeForLocationModal, setSelectedNodeForLocationModal] = useState(null);
    const [parentNodeForLocationModal, setParentNodeForLocationModal] = useState(null);
    const [isShowHelpModal, setIsShowHelpModal] = useState(false);
    const [lastMoveContext, setLastMoveContext] = useState(null);
    const [pendingDeleteNodeId, setPendingDeleteNodeId] = useState(null);
    const [isShowPowertrainInfoModal, setIsShowPowertrainInfoModal] = useState(false);
    const [selectedPowertrainInfo, setSelectedPowertrainInfo] = useState(null);
    const [duplicateNameModalData, setDuplicateNameModalData] = useState({ visible: false, nodeId: null });
    // Stores the drop/move args to auto-execute after the user renames a location from the duplicate popup
    const [pendingMoveAfterRename, setPendingMoveAfterRename] = useState(null);

    // Get user capabilities for Edit Hierarchy button
    const userCapability = useUserCapability();
    const hasHierarchyConfiguration = userCapability?.hasHierarchyConfiguration?.() || false; /*TRUE FOR REC, Country Admin, Principal & PT_DEV */
    const {
        searchTerm,
        setSearchTerm,
        filteredNodes: searchFilteredNodes,
    } = useHierarchySearch({
        nodes: flattenedNodes
    });

    // Apply changes immediately when they come in
    useEffect(() => {
        if (pendingChanges) {
            // Create API request data
            const requestData = createAPIRequestData(pendingChanges, nodeMap, hierarchyRequestData);
            setHierarchyRequestData(requestData);

            // Handle other operations - apply to base tree
            const updatedTree = applyPendingChangesToTree(baseHierarchyTree, pendingChanges);
            setBaseHierarchyTree(updatedTree);

            if (pendingChanges.type === NODE_MODIFICATION_TYPE.MOVE &&
                (pendingChanges.draggedNode?.type === NODE_TYPE.POWERTRAIN ||
                    pendingChanges.powertrainsData?.length > 0)) {
                const count = pendingChanges.powertrainsData?.length || 1;
                setLastMoveContext(prev => ({
                    type: NODE_TYPE.POWERTRAIN,
                    count: (prev?.type === NODE_TYPE.POWERTRAIN ? prev.count || 0 : 0) + count
                }));
            }

            // Clear pending changes after applying
            setPendingChanges(null);
        }
    }, [pendingChanges]);

    // Chain DELETE after powertrain MOVE is processed (when deleting a location with powertrain children)
    useEffect(() => {
        if (pendingDeleteNodeId && !pendingChanges) {
            setPendingChanges({
                type: NODE_MODIFICATION_TYPE.DELETE,
                siteId: findContainingSite(nodeMap, pendingDeleteNodeId),
                locationId: pendingDeleteNodeId,
                name: nodeMap[pendingDeleteNodeId]?.name
            });
            setPendingDeleteNodeId(null);
        }
    }, [pendingDeleteNodeId, pendingChanges, nodeMap]);

    // Clean up adding state when edit mode is disabled
    useEffect(() => {
        if (!isEditHierarchy) {
            if (pendingChanges?.type === NODE_MODIFICATION_TYPE.TEMP_INPUT) {
                setPendingChanges(null);
                const updatedTree = removeTempInputNodes(baseHierarchyTree);
                setBaseHierarchyTree(updatedTree);
            }
            setLastMoveContext(null);
        }
    }, [isEditHierarchy]);

    const handleDrop = useCallback((draggedNodes, targetNode, position) => {
        const draggedNode = draggedNodes[0];

        // Validate duplicate location name at the drop destination
        if (draggedNode?.type === NODE_TYPE.LOCATION) {
            const draggedName = draggedNode.name;
            let siblingsAtDestination;
            if (position === DROP_POSITION.INSIDE) {
                siblingsAtDestination = nodeMap[targetNode.id]?.children || [];
            } else {
                const parent = findParentNode(nodeMap, targetNode.id);
                siblingsAtDestination = nodeMap[parent?.id]?.children || [];
            }
            const hasDuplicate = siblingsAtDestination.some(
                child => child.type === NODE_TYPE.LOCATION &&
                    child.name === draggedName &&
                    child.id !== draggedNode.id
            );
            if (hasDuplicate) {
                setDuplicateNameModalData({ visible: true, nodeId: draggedNode.id });
                setPendingMoveAfterRename({
                    type: PENDING_MOVE_TYPE.DROP,
                    sourceNodeId: draggedNode.id,
                    targetNodeId: targetNode.id,
                    position,
                    expectedName: null
                });
                return;
            }
        }

        let powertrainsData = [];
        let assetsData = [];
        if (draggedNode.type === NODE_TYPE.POWERTRAIN) {
            powertrainsData = draggedNodes;
        }
        if (draggedNode.type === NODE_TYPE.ASSET) {
            assetsData = draggedNodes;
        }
        const parentId = draggedNode.type === NODE_TYPE.ASSET
            ? draggedNode?.data?.parentId
            : findParentNode(nodeMap, draggedNode.id)?.id;

        setPendingChanges({
            type: NODE_MODIFICATION_TYPE.MOVE,
            sourceNodeId: draggedNode.id,
            targetNodeId: targetNode.id,
            siteId: draggedNode.data?.siteId,
            name: draggedNode.name,
            draggedNode: draggedNode,
            targetNode: targetNode,
            parentId,
            position,
            powertrainsData,
            assetsData
        });
    }, [nodeMap]);

    // Handle adding a new child node
    const handleAddChild = useCallback((parentId) => {
        setPendingChanges({
            type: NODE_MODIFICATION_TYPE.TEMP_INPUT,
            parentId: parentId
        });

        // Auto-expand the parent node to show the new input
        if (!expandedIds.includes(parentId)) {
            setExpandedIds([...expandedIds, parentId]);
        }
    }, [expandedIds]);

    // Handle canceling the new node
    const handleCancelNewNode = useCallback(() => {
        const updatedTree = removeTempInputNodes(baseHierarchyTree);
        setBaseHierarchyTree(updatedTree);
        setPendingChanges(null);
    }, [baseHierarchyTree]);

    // Handle saving the new node
    const handleSaveNewNode = useCallback((name, node, setEditError) => {
        if (name?.trim() && node?.parentId) {
            const siblingLocations = findSiblingLocations(nodeMap, node.id);

            // Check if name already exists in sibling locations
            const trimmedName = name.trim();
            const nameExists = siblingLocations.some(sibling => sibling.name === trimmedName);
            if (nameExists) {
                setEditError(translate('ABB.Powertrain.Frontend.locationNameExistsErrorMessage'));
            } else {
                setEditError(null);
                setPendingChanges({
                    type: NODE_MODIFICATION_TYPE.ADD,
                    parentId: node.parentId,
                    siteId: findContainingSite(nodeMap, node.parentId),
                    name: trimmedName,
                    locationId: `location-temp-${Date.now()}`,
                });
            }
        } else {
            handleCancelNewNode();
        }
    }, [nodeMap]);

    // Handle renaming a node
    const handleRenameNode = useCallback((nodeId, newName) => {
        if (!nodeId || !newName) {
            return;
        }

        // Add rename operation to pending changes
        setPendingChanges({
            type: NODE_MODIFICATION_TYPE.RENAME,
            parentId: selectedNodeForLocationModal?.parentId,
            siteId: findContainingSite(nodeMap, nodeId),
            name: newName.trim(),
            locationId: nodeId,
        });

        // If this rename was triggered from the duplicate-name popup, record the new name
        // so the auto-move effect knows when the nodeMap has been updated
        if (pendingMoveAfterRename &&
            (pendingMoveAfterRename.sourceNodeId === nodeId || pendingMoveAfterRename.nodeId === nodeId)) {
            setPendingMoveAfterRename(prev => ({ ...prev, expectedName: newName.trim() }));
        }

    }, [nodeMap, selectedNodeForLocationModal, pendingMoveAfterRename]);

    // Handle deleting a node
    const handleDeleteNode = useCallback((nodeId) => {
        if (!nodeId) {
            return;
        }

        // Collect all powertrain descendants to move to the site before deleting
        const collectPowertrainDescendants = (id) => {
            const node = nodeMap[id];
            if (!node?.children?.length) {
                return [];
            }
            const powertrains = [];
            const traverse = (children) => {
                for (const child of children) {
                    if (child.data?.type === NODE_TYPE.POWERTRAIN) {
                        powertrains.push(child);
                    }
                    if (child.children?.length > 0) {
                        traverse(child.children);
                    }
                }
            };
            traverse(node.children);
            return powertrains;
        };

        const powertrainNodes = collectPowertrainDescendants(nodeId);
        const siteId = findContainingSite(nodeMap, nodeId);
        const siteNode = nodeMap[siteId];

        if (powertrainNodes.length > 0 && siteNode) {
            // Move powertrains to site first; DELETE will be queued after MOVE is processed
            handleDrop(powertrainNodes, siteNode, DROP_POSITION.INSIDE);
            setPendingDeleteNodeId(nodeId);
        } else {
            // No powertrains, delete directly
            setPendingChanges({
                type: NODE_MODIFICATION_TYPE.DELETE,
                siteId: findContainingSite(nodeMap, nodeId),
                locationId: nodeId,
                name: nodeMap[nodeId]?.name
            });
        }

    }, [nodeMap, handleDrop]);

    // Handle moving a node to a new location
    const handleMoveNode = useCallback((nodeId, destinationId) => {
        if (!nodeId || !destinationId) {
            return;
        }

        // Validate duplicate location name at the destination
        const movingNode = nodeMap[nodeId];
        if (movingNode?.type === NODE_TYPE.LOCATION) {
            const destinationChildren = nodeMap[destinationId]?.children || [];
            const hasDuplicate = destinationChildren.some(
                child => child.type === NODE_TYPE.LOCATION &&
                    child.name === movingNode.name &&
                    child.id !== nodeId
            );
            if (hasDuplicate) {
                setDuplicateNameModalData({ visible: true, nodeId: nodeId });
                setPendingMoveAfterRename({
                    type: PENDING_MOVE_TYPE.MOVE,
                    nodeId,
                    destinationId,
                    expectedName: null
                });
                return;
            }
        }

        // Add move operation to pending changes
        setPendingChanges({
            type: NODE_MODIFICATION_TYPE.MOVE,
            sourceNodeId: nodeId,
            targetNodeId: destinationId,
            siteId: findContainingSite(nodeMap, nodeId),
            parentId: findParentNode(nodeMap, nodeId)?.id,
            draggedNode: nodeMap[nodeId],
            targetNode: nodeMap[destinationId],
            name: nodeMap[nodeId]?.name,
            position: DROP_POSITION.INSIDE
        });

    }, [nodeMap]);

    // After a rename triggered from the duplicate popup, auto-execute the original move
    // once nodeMap reflects the new name (i.e., the rename has fully propagated)
    useEffect(() => {
        if (!pendingMoveAfterRename?.expectedName || pendingChanges || showLocationModal) {
            return;
        }
        const { type, sourceNodeId, targetNodeId, position, nodeId, destinationId, expectedName } = pendingMoveAfterRename;
        const movedNodeId = type === PENDING_MOVE_TYPE.DROP ? sourceNodeId : nodeId;
        // Guard: wait until nodeMap carries the renamed value before proceeding
        if (nodeMap[movedNodeId]?.name !== expectedName) {
            return;
        }
        setPendingMoveAfterRename(null);
        if (type === PENDING_MOVE_TYPE.DROP) {
            const freshNode = nodeMap[sourceNodeId];
            const freshTarget = nodeMap[targetNodeId];
            if (freshNode && freshTarget) {
                handleDrop([freshNode], freshTarget, position);
            }
        } else {
            handleMoveNode(nodeId, destinationId);
        }
    }, [pendingMoveAfterRename, pendingChanges, showLocationModal, nodeMap, handleDrop, handleMoveNode]);

    const handleRenameFromDuplicate = () => {
        const nodeId = duplicateNameModalData.nodeId;
        const node = nodeMap[nodeId];
        const parent = findParentNode(nodeMap, nodeId);
        setSelectedNodeForLocationModal(node);
        setParentNodeForLocationModal(parent);
        setLocationModalType(hierarchyLocationModalTypes.RENAME);
        setShowLocationModal(true);
        setDuplicateNameModalData({ visible: false, nodeId: null });
    };

    const handleSaveChanges = () => {
        // Use the updated hierarchy data request action with batch support
        const siteIds = getSiteIds(selectedSites);
        updateHierarchyDataRequest(hierarchyRequestData, () => {
            getHierarchyDataRequest(organizations?.[0]?.organizationID, siteIds);
            setIsEditHierarchy(false);
            setPendingChanges(null);
            setHierarchyRequestData([]);
            setLastMoveContext(null);
        }, setHierarchyErrorModal, setSaveFailed, lastMoveContext, nodeMap);
    };

    const tippyContent = (node) => {
        const parent = findParentNode(nodeMap, node.id);

        return (
            <div className='tippy-options-content'>
                <GeneralButton
                    type='discrete'
                    text={translate('ABB.Powertrain.Frontend.renameLocationLabel')}
                    onClick={(e) => {
                        e.stopPropagation(); // Prevent triggering node select
                        setSelectedNodeForLocationModal(node);
                        setParentNodeForLocationModal(parent);
                        setLocationModalType(hierarchyLocationModalTypes.RENAME);
                        setShowLocationModal(true);
                    }}
                />
                <GeneralButton
                    type='discrete'
                    text={translate('ABB.Powertrain.Frontend.moveToLocationLabel')}
                    onClick={(e) => {
                        e.stopPropagation(); // Prevent triggering node select
                        setSelectedNodeForLocationModal(node);
                        setParentNodeForLocationModal(parent);
                        setLocationModalType(hierarchyLocationModalTypes.MOVE);
                        setShowLocationModal(true);
                    }}
                />
                <GeneralButton
                    type='discrete'
                    text={translate('ABB.Powertrain.Frontend.deleteLocationLabel')}
                    onClick={(e) => {
                        e.stopPropagation(); // Prevent triggering node select
                        setSelectedNodeForLocationModal(node);
                        setParentNodeForLocationModal(parent);
                        setLocationModalType(hierarchyLocationModalTypes.DELETE);
                        setShowLocationModal(true);
                    }}
                />
            </div>
        );
    };

    const renderActions = (node) => {
        if (isEditHierarchy) {
            if (node.data?.type === NODE_TYPE.LOCATION) {
                const depthCount = getLocationDepth(nodeMap, node.id);
                return <>
                    {depthCount < 5 &&
                        <GeneralButton
                            type='normal'
                            text={''}
                            icon={IconPlus({ width: '16px', height: '16px' })}
                            onClick={() => handleAddChild(node.id)}
                        />
                    }
                    <GeneralButton
                        className='drag-icon'
                        type='normal'
                        text={''}
                        icon={IconDragEqual({ width: '16px', height: '16px' })}
                    />
                    <Tippy
                        content={tippyContent(node)}
                        className='hierarchy-options-tippy'
                        interactive={true}
                        interactiveBorder={10}
                        animation='scale'
                        theme='light-border'
                        trigger='mouseenter'
                        placement='bottom-start'
                        maxWidth='500px'
                    >
                        <button
                            className='options-button'
                            aria-label={translate('ABB.Powertrain.Frontend.optionsLabel')}
                        >
                            <IconKebab width={20} height={20} />
                        </button>
                    </Tippy>
                </>;
            } else if (node.data?.type === NODE_TYPE.SITE) {
                return <GeneralButton
                    type='normal'
                    text={''}
                    icon={IconPlus({ width: '16px', height: '16px' })}
                    onClick={() => handleAddChild(node.id)}
                />;
            } else if (node.data?.type === NODE_TYPE.POWERTRAIN && !node?.isExternalNode) {
                return <>
                    <GeneralButton
                        className='info-icon'
                        type='normal'
                        text={''}
                        icon={IconInfo({ width: '14px', height: '14px', viewBox: '0 0 14 14' })}
                        onClick={() => {
                            setIsShowPowertrainInfoModal(true);
                            setSelectedPowertrainInfo(node.data);
                        }}
                    />
                    <GeneralButton
                        className='drag-icon'
                        type='normal'
                        text={''}
                        icon={IconDragEqual({ width: '16px', height: '16px' })}
                    />
                </>;
            }
        }
        return null;
    };

    const isDisableAddPowertrain = useMemo(() => {
        return selectedIds?.[0] && [NODE_TYPE.LOCATION, NODE_TYPE.SITE].includes(nodeMap[selectedIds[0]]?.data?.type);
    }, [selectedIds, nodeMap]);

    const handleCancelUpdate = () => {
        const organizationID = organizations?.[0]?.organizationID;
        const siteIds = getSiteIds(selectedSites);
        handleCancelNewNode();
        setPendingChanges(null);
        setIsEditHierarchy(false);
        setHierarchyRequestData([]);
        getHierarchyDataRequest(organizationID, siteIds);
    };

    // Render edit button - only shown when not in edit mode and user has capability
    const renderEditButton = () => {
        if (!isEditHierarchy && hasHierarchyConfiguration) {
            return (
                <div className='edit-hierarchy-container'>
                    <button
                        className='edit-hierarchy-button'
                        onClick={() => setIsEditHierarchy(true)}
                        aria-label={translate('ABB.Powertrain.Frontend.ptToolsPowertrainConfigurationTab_EditHierarchyButton')}
                    >
                        <IconEdit width={'16px'} height={'16px'} />
                        <span>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainConfigurationTab_EditHierarchyButton')}</span>
                    </button>
                </div>
            );
        }
        return null;
    };

    // Render edit footer - only shown when in edit mode
    const renderEditFooter = () => {
        if (isEditHierarchy) {
            return (
                <div className='edit-footer'>
                    <GeneralButton
                        type='discreet'
                        className='cancel-button'
                        text={translate('ABB.Powertrain.Frontend.cancelLabel')}
                        onClick={handleCancelUpdate}
                    />
                    <GeneralButton
                        type='primary'
                        className='accept-button'
                        onClick={handleSaveChanges}
                        text={translate('ABB.Powertrain.Frontend.saveButton')}
                        disabled={!hierarchyRequestData || hierarchyRequestData?.length === 0}
                    />
                </div>
            );
        }
        return null;
    };

    return (

        <div className={`left-panel-content ${isEditHierarchy ? 'hierarchy-view-edit-container' : ''}`}>
            {!isEditHierarchy &&
                <GeneralButton
                    className='create-powertrain-button'
                    type='primary'
                    text={translate('ABB.Powertrain.Frontend.ptToolsPowertrainConfigurationTab_AddNewPowertrainButton')}
                    icon={IconAdd({ ...UI_CONSTANTS.BUTTON_ICON_SIZE, color: colors.white })}
                    onClick={() => {
                        // Pass selected node data to wizard
                        const selectedNode = selectedIds?.[0] ? nodeMap[selectedIds[0]] : null;
                        if (selectedNode) {
                            setWizardSelectedNode(selectedNode);
                        }
                        setPowertrainWizardVisible(true);
                    }}
                    disabled={!isDisableAddPowertrain}
                />
            }
            <HierarchyLeftPanelHeader
                setSearchboxFilter={setSearchTerm}
                searchboxFilter={searchTerm}
                isEditHierarchy={isEditHierarchy}
                setIsShowHelpModal={setIsShowHelpModal}
            />
            <HierarchyLeftContent
                transformedHierarchyData={searchFilteredNodes}
                isSearchActive={Boolean(searchTerm)}
                nodeMap={nodeMap}
                selectedIds={selectedIds}
                expandedIds={expandedIds}
                setSelectedIds={setSelectedIds}
                setExpandedIds={setExpandedIds}
                handleDrop={handleDrop}
                isEditHierarchy={isEditHierarchy}
                renderActions={renderActions}
                handleSaveNewNode={handleSaveNewNode}
                handleCancelNewNode={handleCancelNewNode}
                setSelectedPowertrain={setSelectedPowertrain}
                setSearchTerm={setSearchTerm}
                filteredPowertrains={filteredPowertrains}
                updateHierarchyLoadingStatus={updateHierarchyLoadingStatus}
                setSaveFailed={setSaveFailed}
                setPartialPowertrainFailure={setPartialPowertrainFailure}
                setHierarchyErrorModal={setHierarchyErrorModal}
            />
            {renderEditButton()}
            {renderEditFooter()}
            <HierarchyLocationModal
                show={showLocationModal}
                close={() => {
                    setShowLocationModal(false);
                    setParentNodeForLocationModal(null);
                    // If rename was opened from the duplicate popup but user cancelled
                    // (no new name captured), discard the pending auto-move
                    if (pendingMoveAfterRename && !pendingMoveAfterRename.expectedName) {
                        setPendingMoveAfterRename(null);
                    }
                }}
                handleRenameNode={handleRenameNode}
                handleDeleteNode={handleDeleteNode}
                handleMoveNode={handleMoveNode}
                loadingStatus={null}
                type={locationModalType}
                selectedNode={selectedNodeForLocationModal}
                parentNode={parentNodeForLocationModal}
                hierarchyData={baseHierarchyTree}
                organizations={organizations}
            />
            <HierarchyInfoModal
                isOpen={isShowHelpModal}
                onClose={() => setIsShowHelpModal(false)}
            />
            <HierarchyErrorModal
                isOpen={hierarchyErrorModalVisible}
                onClose={() => { setHierarchyErrorModal(false); setSaveFailed(false); setPartialPowertrainFailure(false); }}
                isSaveFailed={isSaveFailed}
                isPartialPowertrainFailure={isPartialPowertrainFailure}
                handleCancelUpdate={handleCancelUpdate}
            />
            <HierarchyPowertrainInfoModal
                isOpen={isShowPowertrainInfoModal}
                onClose={() => {
                    setIsShowPowertrainInfoModal(false);
                    setSelectedPowertrainInfo(null);
                }}
                selectedPowertrainInfo={selectedPowertrainInfo}
            />
            <HierarchyDuplicateNameModal
                isOpen={duplicateNameModalData.visible}
                onClose={() => setDuplicateNameModalData({ visible: false, nodeId: null })}
                onRename={handleRenameFromDuplicate}
            />
        </div>
    );
};

HierarchyLeftPanel.propTypes = {
    isEditHierarchy: PropTypes.bool,
    setIsEditHierarchy: PropTypes.func,
    setPowertrainWizardVisible: PropTypes.func,
    filteredPowertrains: PropTypes.array,
    hierarchyData: PropTypes.array,
    updateHierarchyDataRequest: PropTypes.func,
    organizations: PropTypes.array,
    selectedIds: PropTypes.array,
    setSelectedIds: PropTypes.func,
    nodeMap: PropTypes.object,
    baseHierarchyTree: PropTypes.array,
    setBaseHierarchyTree: PropTypes.func,
    expandedIds: PropTypes.array,
    setExpandedIds: PropTypes.func,
    flattenedNodes: PropTypes.array,
    hierarchyRequestData: PropTypes.array,
    setHierarchyRequestData: PropTypes.func,
    setSaveFailed: PropTypes.func,
    setPartialPowertrainFailure: PropTypes.func,
    setHierarchyErrorModal: PropTypes.func,
    getHierarchyDataRequest: PropTypes.func,
    updateHierarchyLoadingStatus: PropTypes.func,
    hierarchyErrorModalVisible: PropTypes.bool,
    isSaveFailed: PropTypes.bool,
    isPartialPowertrainFailure: PropTypes.bool,
    setWizardSelectedNode: PropTypes.func,
    setSelectedPowertrain: PropTypes.func,
    selectedSites: PropTypes.array
};

export default HierarchyLeftPanel;
